import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { useSite, scrollToId, P2P_URL } from "@/lib/site";
import { LogoMark } from "./LogoMark";
import { LangToggle, ThemeToggle } from "./Toggles";

export const Header = () => {
  const { t } = useSite();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const go = (id) => { setOpen(false); scrollToId(id); };

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${scrolled || open ? "glass border-b border-[var(--line)]" : ""}`} data-testid="site-header">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <button onClick={() => go("top")} className="flex items-center gap-2.5" data-testid="header-logo">
          <LogoMark />
          <span className="font-display text-sm font-extrabold uppercase tracking-[0.14em]">Resilience <span className="text-[var(--violet-h)]">Brothers</span></span>
        </button>
        <nav className="hidden items-center gap-8 md:flex">
          {t.nav.map((n) => (
            <button key={n.id} onClick={() => go(n.id)} data-testid={`nav-${n.id}`} className="text-sm text-[var(--muted)] transition-colors hover:text-[var(--text)]">{n.label}</button>
          ))}
        </nav>
        <div className="hidden items-center gap-3 md:flex">
          <LangToggle />
          <ThemeToggle />
          <a href={P2P_URL} target="_blank" rel="noopener noreferrer" data-testid="header-p2p-cta" className="pill-primary inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold">
            {t.ctaP2P} <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
        <button className="grid h-10 w-10 place-items-center rounded-full border border-[var(--line)] md:hidden" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden md:hidden" data-testid="mobile-menu">
            <div className="flex flex-col gap-1 px-5 pb-6">
              {t.nav.map((n) => (
                <button key={n.id} onClick={() => go(n.id)} data-testid={`mobile-nav-${n.id}`} className="border-b border-[var(--line)] py-4 text-left font-display text-2xl font-extrabold">{n.label}</button>
              ))}
              <div className="mt-4 flex items-center gap-3">
                <LangToggle id="mobile" />
                <ThemeToggle id="mobile" />
              </div>
              <a href={P2P_URL} target="_blank" rel="noopener noreferrer" data-testid="mobile-p2p-cta" className="pill-primary mt-4 inline-flex items-center justify-center gap-1.5 rounded-full px-5 py-3 font-semibold">
                {t.ctaP2P} <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
